import React, { useEffect, useRef, useState } from "react";
import { Maximize2, RotateCcw, ZoomIn, ZoomOut, FileImage, FileText } from "lucide-react";
import { atlasLabel, normalizePortugueseEncoding } from "../../utils/atlasText";
import type { AtlasLanguage } from "../../translations";
import { atlasColors } from "./atlasTheme";

type Props = {
  language: AtlasLanguage;
  imageUrl?: string | null;
  fileName?: string;
  mimeType?: string;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const inferMimeType = (imageUrl?: string | null, mimeType?: string | null) => {
  const normalizedMime = String(mimeType || "").toLowerCase().trim();
  if (normalizedMime) return normalizedMime;

  const normalizedUrl = String(imageUrl || "").toLowerCase();
  if (normalizedUrl.startsWith("data:application/pdf")) return "application/pdf";
  if (normalizedUrl.startsWith("data:image/")) {
    const match = normalizedUrl.match(/^data:([^;,]+)/);
    return match?.[1] || "image/*";
  }

  return "";
};

export function SurgicalPlanOriginalImageViewer({ language, imageUrl, fileName, mimeType }: Props) {
  const title = atlasLabel(language, "originalImageViewerTitle");
  const resetLabel = atlasLabel(language, "originalImageViewerReset");
  const fitLabel = atlasLabel(language, "originalImageViewerFit");
  const zoomInLabel = atlasLabel(language, "originalImageViewerZoomIn");
  const zoomOutLabel = atlasLabel(language, "originalImageViewerZoomOut");
  const pdfUnavailableLabel = atlasLabel(language, "originalImageViewerPdfUnavailable");
  const altLabel = atlasLabel(language, "originalImageViewerAlt");

  const viewerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({ startX: 0, startY: 0, startPanX: 0, startPanY: 0 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const resolvedMimeType = inferMimeType(imageUrl, mimeType);
  const isPdf = resolvedMimeType.includes("pdf");

  const clampPan = (nextPan: { x: number; y: number }, nextZoom: number) => {
    const rect = viewerRef.current?.getBoundingClientRect();
    const width = Math.max(1, rect?.width || 1);
    const height = Math.max(1, rect?.height || 1);
    const maxPanX = Math.max(0, Math.round(width * 0.32 * Math.max(0, nextZoom - 1)) + 36);
    const maxPanY = Math.max(0, Math.round(height * 0.32 * Math.max(0, nextZoom - 1)) + 36);

    return {
      x: clamp(nextPan.x, -maxPanX, maxPanX),
      y: clamp(nextPan.y, -maxPanY, maxPanY),
    };
  };

  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setIsDragging(false);
  };

  const fitToScreen = () => {
    resetView();
  };

  const changeZoom = (delta: number) => {
    if (isPdf) return;
    const nextZoom = clamp(Number((zoom + delta).toFixed(2)), 1, 5);
    setZoom(nextZoom);
    if (nextZoom <= 1.01) {
      setPan({ x: 0, y: 0 });
    } else {
      setPan((currentPan) => clampPan(currentPan, nextZoom));
    }
  };

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (isPdf) return;
    event.preventDefault();
    event.stopPropagation();
    const direction = event.deltaY > 0 ? -1 : 1;
    changeZoom(direction * 0.14);
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isPdf || zoom <= 1.01 || event.button !== 0) return;
    event.preventDefault();
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startPanX: pan.x,
      startPanY: pan.y,
    };
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (event: MouseEvent) => {
      event.preventDefault();
      const nextPan = {
        x: dragRef.current.startPanX + (event.clientX - dragRef.current.startX),
        y: dragRef.current.startPanY + (event.clientY - dragRef.current.startY),
      };
      setPan(clampPan(nextPan, zoom));
    };

    const handleUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [isDragging, zoom]);

  useEffect(() => {
    const el = viewerRef.current;
    if (!el) return;

    const handleNativeWheel = (event: WheelEvent) => {
      if (isPdf) return;
      event.preventDefault();
      event.stopPropagation();
      const direction = event.deltaY > 0 ? -1 : 1;
      changeZoom(direction * 0.14);
    };

    el.addEventListener("wheel", handleNativeWheel, { passive: false });

    return () => {
      el.removeEventListener("wheel", handleNativeWheel);
    };
  }, [changeZoom, isPdf]);

  if (!imageUrl) {
    return null;
  }

  return (
    <section
      className="overflow-hidden rounded-[24px] border"
      style={{
        background: "linear-gradient(180deg, rgba(2,6,23,0.96), rgba(15,23,42,0.98))",
        borderColor: atlasColors.borderMuted,
        boxShadow: "0 18px 60px rgba(0, 0, 0, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.04)",
      }}
    >
      <div className="flex flex-col gap-3 border-b px-4 py-4 sm:flex-row sm:items-center sm:justify-between" style={{ borderColor: atlasColors.borderMuted }}>
        <div className="min-w-0">
          <div className="text-[11px] font-black uppercase tracking-[0.24em]" style={{ color: atlasColors.accent }}>
            {normalizePortugueseEncoding(title)}
          </div>
          {fileName ? (
            <div className="mt-1 truncate text-[13px] font-semibold" style={{ color: atlasColors.textPrimary }}>
              {normalizePortugueseEncoding(fileName)}
            </div>
          ) : null}
        </div>

        {!isPdf ? (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => changeZoom(-0.18)}
              className="inline-flex items-center gap-2 rounded-full border px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] transition hover:bg-cyan-500/10"
              style={{ borderColor: atlasColors.borderMuted, color: atlasColors.textPrimary }}
              title={zoomOutLabel}
            >
              <ZoomOut className="h-3.5 w-3.5" />
              {normalizePortugueseEncoding(zoomOutLabel)}
            </button>
            <button
              type="button"
              onClick={fitToScreen}
              className="inline-flex items-center gap-2 rounded-full border px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] transition hover:bg-cyan-500/10"
              style={{ borderColor: atlasColors.borderMuted, color: atlasColors.textPrimary }}
              title={fitLabel}
            >
              <Maximize2 className="h-3.5 w-3.5" />
              {normalizePortugueseEncoding(fitLabel)}
            </button>
            <button
              type="button"
              onClick={resetView}
              className="inline-flex items-center gap-2 rounded-full border px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] transition hover:bg-cyan-500/10"
              style={{ borderColor: atlasColors.borderMuted, color: atlasColors.textPrimary }}
              title={resetLabel}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {normalizePortugueseEncoding(resetLabel)}
            </button>
            <button
              type="button"
              onClick={() => changeZoom(0.18)}
              className="inline-flex items-center gap-2 rounded-full border px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] transition hover:bg-cyan-500/10"
              style={{ borderColor: atlasColors.borderMuted, color: atlasColors.textPrimary }}
              title={zoomInLabel}
            >
              <ZoomIn className="h-3.5 w-3.5" />
              {normalizePortugueseEncoding(zoomInLabel)}
            </button>
            <div className="rounded-full border px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em]" style={{ borderColor: atlasColors.borderMuted, color: atlasColors.textSecondary }}>
              {Math.round(zoom * 100)}%
            </div>
          </div>
        ) : null}
      </div>

      <div
        ref={viewerRef}
      className="relative h-[420px] overflow-hidden bg-black sm:h-[460px] lg:h-[500px]"
      onMouseDown={handleMouseDown}
      style={{ cursor: isPdf ? "default" : zoom > 1.01 ? (isDragging ? "grabbing" : "grab") : "zoom-in" }}
    >
        {isPdf ? (
          <div className="flex h-full items-center justify-center p-6 text-center">
            <div className="max-w-md rounded-2xl border px-5 py-4" style={{ borderColor: atlasColors.borderMuted, background: "rgba(2, 6, 23, 0.72)" }}>
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full border" style={{ borderColor: atlasColors.borderMuted, color: atlasColors.accent }}>
                <FileText className="h-5 w-5" />
              </div>
              <div className="text-[12px] font-black uppercase tracking-[0.22em]" style={{ color: atlasColors.accent }}>
                PDF
              </div>
              <div className="mt-3 text-[14px] leading-[1.6]" style={{ color: atlasColors.textPrimary }}>
                {normalizePortugueseEncoding(pdfUnavailableLabel)}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center p-4">
            <img
              src={imageUrl}
              alt={normalizePortugueseEncoding(altLabel)}
              draggable={false}
              onLoad={() => {
                if (zoom <= 1.01) {
                  setPan({ x: 0, y: 0 });
                }
              }}
              className="max-h-full max-w-full select-none object-contain"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transition: isDragging ? "none" : "transform 0.08s linear",
                willChange: "transform",
                userSelect: "none",
              }}
            />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t px-4 py-3 text-[11px] font-medium" style={{ borderColor: atlasColors.borderMuted, color: atlasColors.textSecondary }}>
        <div className="flex items-center gap-2">
          <FileImage className="h-3.5 w-3.5" />
          <span>{normalizePortugueseEncoding(title)}</span>
        </div>
        <div>{isPdf ? "PDF" : `${Math.round(zoom * 100)}%`}</div>
      </div>
    </section>
  );
}

export default SurgicalPlanOriginalImageViewer;
